// Shown after Google sign-in when the customer has no Libyan phone number yet.
// We need a phone to deliver orders + to identify duplicates. Once saved, the user
// is routed to the customer app.
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { extractError } from "@/api/client";
import { useAuth } from "@/contexts/AuthContext";
import PhoneInput from "@/components/PhoneInput";
import { CheckCircle2, Gift, Phone } from "lucide-react";

export default function CompleteProfile() {
  const { user, loading, refresh } = useAuth();
  const nav = useNavigate();
  const [phone, setPhone] = useState("");
  const [referralCode, setReferralCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (loading) return;
    if (!user) nav("/login", { replace: true });
    else if (user.phone) nav("/", { replace: true });
  }, [user, loading, nav]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      await api.post("/auth/complete-profile", {
        phone: phone,
        referral_code: referralCode.trim() ? referralCode.trim().toUpperCase() : undefined,
      });
      await refresh();
      nav("/", { replace: true });
    } catch (e) { setErr(extractError(e)); }
    finally { setBusy(false); }
  };

  if (loading || !user) return null;

  return (
    <div className="min-h-screen flex items-center justify-center px-4 gradient-warm">
      <div className="w-full max-w-md bg-white rounded-3xl talabya-shadow-soft p-8 animate-fade-up">
        <div className="flex items-center gap-2 mb-4">
          <img src="/icon-192.png" alt="طلبية" className="w-12 h-12 rounded-xl object-cover"
               style={{boxShadow:"0 3px 8px rgba(37,71,208,0.3)"}}/>
          <div>
            <div className="font-black text-xl" style={{fontFamily:"Cairo"}}>طلبية</div>
            <div className="text-xs text-[var(--text-secondary)] -mt-1">إكمال ملفك الشخصي</div>
          </div>
        </div>

        <div className="flex items-center gap-2 text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-xl px-3 py-2 mb-4">
          <CheckCircle2 size={16}/>
          <div className="text-xs">
            أهلاً بك <b>{user.name}</b>! نحتاج فقط لرقم هاتفك حتى نتمكن من توصيل طلباتك.
          </div>
        </div>

        <form onSubmit={submit} className="flex flex-col gap-3">
          <div>
            <label className="text-sm font-bold flex items-center gap-1 mb-1.5">
              <Phone size={13}/> رقم الهاتف (ليبيا)
            </label>
            <PhoneInput
              value={phone}
              onChange={(e164) => setPhone(e164)}
              dataTestid="complete-phone"
            />
          </div>

          <div>
            <label className="text-sm font-bold flex items-center gap-1 mb-1.5">
              <Gift size={13} className="text-emerald-600"/> كود إحالة صديق (اختياري)
            </label>
            <input
              placeholder="ABCD1234"
              className="w-full px-4 py-3 rounded-xl border border-emerald-300 bg-emerald-50/40 focus:bg-white focus:border-emerald-500 focus:outline-none uppercase tracking-widest text-center font-bold"
              value={referralCode}
              onChange={(e) => setReferralCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 8))}
              data-testid="complete-referral"/>
            {referralCode && (
              <div className="text-[11px] text-emerald-700 mt-1">ستحصل على 1 د.ل هدية بعد أول طلب مكتمل</div>
            )}
          </div>

          {err && <div className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg" data-testid="complete-err">{err}</div>}

          <button
            type="submit"
            disabled={busy || !phone}
            className="w-full py-3 rounded-xl bg-primary-brand text-white font-black hover:opacity-90 disabled:opacity-60 talabya-shadow-float"
            data-testid="complete-submit">
            {busy ? "جاري الحفظ..." : "متابعة"}
          </button>
        </form>
      </div>
    </div>
  );
}
